
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('メッセージは500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d33n9m849c5vu889jv8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: formData.email,
          subject: formData.subject,
          category: formData.category,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          email: '',
          subject: '',
          category: 'general',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-black/90 backdrop-blur-sm border-b border-amber-500/20 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-amber-400 cursor-pointer" style={{fontFamily: "Pacifico, serif"}}>奇跡のブラックアウト</Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-white hover:text-amber-400 transition-colors cursor-pointer">ホーム</Link>
              <Link to="/terms" className="text-white hover:text-amber-400 transition-colors cursor-pointer">利用規約</Link>
              <Link to="/privacy" className="text-white hover:text-amber-400 transition-colors cursor-pointer">プライバシーポリシー</Link>
              <Link to="/contact" className="text-amber-400 cursor-pointer">お問い合わせ</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gray-900/50 border border-amber-500/20 rounded-2xl p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-amber-400 mb-8 text-center">お問い合わせ</h1>
          
          <div className="mb-8">
            <p className="text-gray-300 text-center leading-relaxed">
              奇跡のブラックアウトに関するご質問、ご要望、不具合報告などがございましたら,<br />
              以下のフォームよりお気軽にお問い合わせください。
            </p>
          </div>

          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-amber-400 mb-2">
                メールアドレス <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-7
                00 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none transition-colors"
                placeholder="example@example.com"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-semibold text-amber-400 mb-2">
                お問い合わせカテゴリ <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus-border-amber-500 focus:outline-none transition-colors appearance-none cursor-pointer"
                >
                  <option value="general">一般的なお問い合わせ</option>
                  <option value="bug">バグ・不具合報告</option>
                  <option value="feature">機能要望</option>
                  <option value="account">アカウント関連</option>
                  <option value="payment">課金・決済関連</option>
                  <option value="other">その他</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-semibold text-amber-400 mb-2">
                件名 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus-border-amber-500 focus:outline-none transition-colors"
                placeholder="お問い合わせの件名をご入力ください"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-amber-400 mb-2">
                メッセージ <span className="text-red-400">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus-border-amber-500 focus:outline-none transition-colors resize-vertical"
                placeholder="お問い合わせ内容を詳しくご記入ください（500文字以内）"
              />
              <div className="text-right mt-2">
                <span className={`text-sm ${formData.message.length > 500 ? 'text-red-400' : 'text-gray-400'}`}>
                  {formData.message.length}/500文字
                </span>
              </div>
            </div>

            <div className="bg-gray-800/50 border border-amber-500/20 rounded-lg p-4">
              <h3 className="text-amber-400 font-semibold mb-2 flex items-center">
                <i className="ri-information-line mr-2"></i>
                お問い合わせ前にご確認ください
              </h3>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>• 不具合報告の際は、お使いの端末情報（iOS/Android、バージョン等）も併せてお知らせください</li>
                <li>• 回答には1-3営業日程度お時間をいただく場合があります</li>
              </ul>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4">
                <div className="flex items-center text-green-400">
                  <i className="ri-check-line mr-2"></i>
                  <span>お問い合わせを受け付けました。ご返信をお待ちください。</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                <div className="flex items-center text-red-400">
                  <i className="ri-error-warning-line mr-2"></i>
                  <span>送信に失敗しました。時間をおいて再度お試しください。</span>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="bg-gradient-to-r from-red-600 to-amber-500 hover:from-red-700 hover:to-amber-600 disabled:from-gray-600 disabled:to-gray-500 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    送信する
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-12 pt-8 border-t border-amber-500/20">
            <div className="grid md:grid-cols-1 gap-8">
              <div>
                <h3 className="text-xl font-bold text-amber-400 mb-4">緊急のお問い合わせ</h3>
                <p className="text-gray-300 text-sm">
                  アプリが起動しない、課金に関する緊急の問題などは、各アプリストアのサポートへ直接お問い合わせください。
                </p>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-amber-500/20 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 奇跡のブラックアウト. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
